import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraDateField, SpecraFormField, SpecraInput, SpecraOverlayDialog, SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { apiErrorMessage } from '../../lib/api/apiErrorMessage';
import { morgansClient } from '../../lib/connect/clients';
import { GovernmentOrgPicker } from '../Delivery/_shared/components/GovernmentOrgPicker';
import { useTenantId } from '../Delivery/_shared/hooks/useTenantId';
import { EVENT_KINDS, useUpsertEvent } from './eventHooks';
const ids = Testids.newEvent;
const dayToRfc3339 = (day)=>day ? `${day}T00:00:00Z` : '';
/** GRW-36 "New event": title and kind required; creates one `govcon.events` row. */ export function NewEventDialog({ open, onOpenChange, initialTitle, opportunityId, onCreated }) {
    const upsert = useUpsertEvent();
    const tenantId = useTenantId();
    const notices = useQuery({
        queryKey: [
            'event-notice-suggestions',
            tenantId,
            opportunityId
        ],
        enabled: open && !!tenantId && !!opportunityId,
        queryFn: async ()=>(await morgansClient.listCanonicalOpportunityNoticeLinks({
                opportunityId
            })).links
    });
    const noticeIds = [
        ...new Set((notices.data ?? []).map((link)=>link.noticeId).filter(Boolean))
    ];
    const [title, setTitle] = useState('');
    const [kind, setKind] = useState('');
    const [startsOn, setStartsOn] = useState('');
    const [endsOn, setEndsOn] = useState('');
    const [hostId, setHostId] = useState('');
    const [noticeId, setNoticeId] = useState('');
    const [location, setLocation] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [serverError, setServerError] = useState('');
    const inFlight = useRef(false);
    // Every open starts from a fresh draft.
    useEffect(()=>{
        if (!open) return;
        setTitle(initialTitle?.trim() ?? '');
        setKind('');
        setStartsOn('');
        setEndsOn('');
        setHostId('');
        setNoticeId('');
        setLocation('');
        setSubmitted(false);
        setServerError('');
    }, [
        open,
        initialTitle
    ]);
    const errors = {
        title: title.trim() ? '' : 'Title is required.',
        kind: kind ? '' : 'Kind is required.',
        ends: startsOn && endsOn && endsOn < startsOn ? 'Ends cannot be before it starts.' : ''
    };
    const invalid = Boolean(errors.title || errors.kind || errors.ends);
    const submit = async ()=>{
        setSubmitted(true);
        if (invalid || inFlight.current) return;
        inFlight.current = true;
        setServerError('');
        try {
            const event = await upsert.mutateAsync({
                title: title.trim(),
                kind,
                startsAt: dayToRfc3339(startsOn),
                endsAt: dayToRfc3339(endsOn),
                hostGovernmentOrganizationId: hostId,
                noticeId: noticeId.trim(),
                locationText: location.trim()
            });
            onOpenChange(false);
            onCreated(event);
        } catch (err) {
            setServerError(apiErrorMessage(err, 'The event could not be created. Try again.'));
        } finally{
            inFlight.current = false;
        }
    };
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: open,
        onOpenChange: onOpenChange,
        title: "New event",
        testId: ids.dialog,
        footerProps: {
            confirmLabel: upsert.isPending ? 'Creating…' : 'Create event',
            onConfirm: submit,
            confirmProps: {
                disabled: upsert.isPending,
                'data-testid': ids.confirm
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-[var(--space-4)] py-[var(--space-2)]",
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Title",
                    htmlFor: "new-event-title",
                    required: true,
                    error: submitted ? errors.title : undefined,
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        id: "new-event-title",
                        "data-testid": ids.title,
                        value: title,
                        onChange: (e)=>setTitle(e.target.value),
                        placeholder: "VA OIT Industry Day"
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Kind",
                    htmlFor: "new-event-kind",
                    required: true,
                    error: submitted ? errors.kind : undefined,
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        id: "new-event-kind",
                        "data-testid": ids.kind,
                        value: kind,
                        onChange: (e)=>setKind(e.target.value),
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Select a kind"
                            }),
                            EVENT_KINDS.map((k)=>/*#__PURE__*/ _jsx("option", {
                                    value: k.value,
                                    children: k.label
                                }, k.value))
                        ]
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "grid grid-cols-1 gap-[var(--space-4)] sm:grid-cols-2",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraDateField, {
                            label: "Starts",
                            picker: "native",
                            value: startsOn,
                            onValueChange: setStartsOn,
                            "data-testid": ids.starts
                        }),
                        /*#__PURE__*/ _jsx(SpecraDateField, {
                            label: "Ends",
                            picker: "native",
                            value: endsOn,
                            onValueChange: setEndsOn,
                            error: errors.ends || undefined,
                            "data-testid": ids.ends
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(GovernmentOrgPicker, {
                    id: "new-event-host",
                    label: "Host",
                    value: hostId,
                    onChange: setHostId,
                    testid: ids.host
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "SAM notice",
                    htmlFor: "new-event-notice",
                    hint: noticeIds.length ? `Opportunity notice IDs: ${noticeIds.join(', ')}` : 'Optional.',
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        id: "new-event-notice",
                        "data-testid": ids.notice,
                        value: noticeId,
                        onChange: (e)=>setNoticeId(e.target.value)
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Location",
                    htmlFor: "new-event-location",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        id: "new-event-location",
                        "data-testid": ids.location,
                        value: location,
                        onChange: (e)=>setLocation(e.target.value)
                    })
                }),
                serverError && /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "meta",
                    tone: "danger",
                    role: "alert",
                    "data-testid": ids.error,
                    children: serverError
                })
            ]
        })
    });
}
